import { forwardRef, useMemo } from "react";
import { SuggestionListContainer, SuggestionRow, useSuggestionListNav } from "../rich-chip-editor";
import type { ChipListHandle } from "../rich-chip-editor";
import { IdentityAvatar } from "../../data-display/identity-avatar";
import type { IdentityKind } from "../../utils";
import { MatchedText } from "../../data-display/matched-text";
import { inferMentionableType, type MentionableType } from "@quovy/types/chip-doc/mention-types";
import type { Mentionable } from "./types";

export interface MentionListProps {
  items: Mentionable[];
  /** Inserts the mention chip. Attribute keys are the persisted wire format
   *  (see MENTION_NODE / MENTION_TYPE_ATTR in ./types). */
  command: (attrs: { id: string; label: string; mentionType: MentionableType }) => void;
  /** Text typed after `@`; the matching part of each name is emphasised. */
  query?: string;
}

const GROUP_ORDER: { type: MentionableType; label: string }[] = [
  { type: "user", label: "People" },
  { type: "agent", label: "Agents" },
  { type: "tool", label: "Tools" },
];

// Tools have no identity kind, so they fall back to the initials avatar.
const IDENTITY_KIND: Record<MentionableType, IdentityKind | null> = {
  user: "user",
  agent: "agent",
  tool: null,
};

export const MentionList = forwardRef<ChipListHandle, MentionListProps>(
  ({ items, command, query = "" }, ref) => {
    const groups = useMemo(() => {
      return GROUP_ORDER.flatMap((group) => {
        const groupItems = items.filter(
          (item) => (item.type ?? inferMentionableType(item.id)) === group.type,
        );
        return groupItems.length > 0 ? [{ ...group, items: groupItems }] : [];
      });
    }, [items]);

    const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

    const selectItem = (index: number) => {
      const item = flat[index];
      if (item) {
        command({
          id: item.id,
          label: item.name ?? item.username,
          mentionType: item.type ?? inferMentionableType(item.id),
        });
      }
    };

    const { selectedIndex, setSelectedIndex, itemRef } = useSuggestionListNav<HTMLButtonElement>(
      ref,
      flat.length,
      selectItem,
      items,
    );

    let renderIndex = 0;

    return (
      <SuggestionListContainer
        className="mention-popup w-max min-w-72 max-w-[min(75vw,36rem)]"
        empty={flat.length === 0}
        emptyMessage="No matches"
      >
        {groups.map((group) => (
          <div key={group.type} className="pb-0.5">
            {/* A lone group needs no label; the avatar shape already says what it is. */}
            {groups.length > 1 && (
              <div className="px-2 pt-1.5 pb-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
            )}
            {group.items.map((item) => {
              const index = renderIndex++;
              return (
                <SuggestionRow
                  key={item.id}
                  index={index}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  itemRef={itemRef}
                  onSelect={selectItem}
                  className="py-3"
                >
                  <IdentityAvatar
                    kind={IDENTITY_KIND[item.type ?? inferMentionableType(item.id)]}
                    id={item.id}
                    name={item.name ?? item.username}
                    avatarUrl={item.avatarUrl}
                  />
                  <MatchedText
                    text={item.name ?? item.username}
                    query={query}
                    selected={index === selectedIndex}
                    className="font-medium"
                  />
                </SuggestionRow>
              );
            })}
          </div>
        ))}
      </SuggestionListContainer>
    );
  },
);

MentionList.displayName = "MentionList";
